/**
 * Features API — Endpoints 3, 8, 9, 11, 13
 *
 * API 3  : POST   /api/releases/{id}/features              → Add a feature to a DRAFT release
 * API 8  : GET    /api/features/{id}/clients               → Which clients have adopted a feature?
 * API 9  : GET    /api/features/search?featureName=...     → Search features by name
 * API 11 : GET    /api/releases/{id}/features              → Get all features in a release
 * API 13 : DELETE /api/releases/{id}/features/{featureId}  → Remove a feature from a release
 */
import { apiFetch, buildUrl } from "./apiClient";

/**
 * API 9 — Search features by name (for autocomplete).
 * Called with a debounced user-typed string.
 *
 * @param {string} featureName - partial or full feature name, e.g. "dark"
 * @returns {Promise<{ features: { featureId, featureName, releaseId, releaseType, version }[], count: number }>}
 */
export async function searchFeaturesByName(featureName) {
    if (!featureName || !featureName.trim()) throw new Error("featureName is required.");
    const path = buildUrl("/api/features/search", { featureName: featureName.trim() });
    return apiFetch(path);
}

/**
 * API 3 — Add a feature definition to a DRAFT release.
 * Can be called multiple times (once per feature).
 *
 * @param {string} releaseId - must be in DRAFT status
 * @param {{ featureId: string, featureName: string, version?: string, releaseType: "GENERIC"|"CLIENT" }} payload
 * @returns {Promise<{ data: Feature, meta: object }>}
 */
export async function addFeatureToRelease(releaseId, payload) {
    const { featureId, featureName, version = "1.0.0", releaseType } = payload || {};
    if (!releaseId) throw new Error("releaseId is required.");
    if (!featureId) throw new Error("featureId is required.");
    if (!featureName) throw new Error("featureName is required.");
    if (!releaseType) throw new Error("releaseType is required (GENERIC or CLIENT).");

    return apiFetch(
        `/api/releases/${encodeURIComponent(releaseId)}/features`,
        {
            method: "POST",
            body: JSON.stringify({ featureId, featureName, version, releaseType }),
        }
    );
}

/**
 * API 8 — Get all clients that have adopted a specific feature.
 * Useful for "feature adoption" analytics.
 *
 * @param {string} featureId - e.g. "FEATURE_DARK_MODE"
 * @param {number} [limit=20]
 * @param {string} [cursor]
 * @returns {Promise<{ data: { clientId, featureId, releaseId, releaseDate, status }[], meta: object }>}
 */
export async function fetchFeatureClients(featureId, limit = 20, cursor) {
    if (!featureId) throw new Error("featureId is required.");
    const path = buildUrl(
        `/api/features/${encodeURIComponent(featureId)}/clients`,
        { limit, cursor }
    );
    return apiFetch(path);
}

/**
 * API 11 — Get all features currently in a release.
 * Call this on the Release Detail page along with API 10 (parallel fetch).
 *
 * @param {string} releaseId
 * @returns {Promise<{ data: { releaseId: string, features: Feature[], count: number }, meta: object }>}
 */
export async function fetchReleaseFeatures(releaseId) {
    if (!releaseId) throw new Error("releaseId is required.");
    return apiFetch(
        `/api/releases/${encodeURIComponent(releaseId)}/features`
    );
}

/**
 * API 13 — Remove a feature from a release.
 * Only allowed when the release is in DRAFT or READY state.
 * RELEASED releases return 409.
 *
 * @param {string} releaseId
 * @param {string} featureId
 * @returns {Promise<{ data: { releaseId: string, featureId: string, deleted: boolean }, meta: object }>}
 */
export async function deleteFeatureFromRelease(releaseId, featureId) {
    if (!releaseId) throw new Error("releaseId is required.");
    if (!featureId) throw new Error("featureId is required.");
    return apiFetch(
        `/api/releases/${encodeURIComponent(releaseId)}/features/${encodeURIComponent(featureId)}`,
        { method: "DELETE" }
    );
}
